import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorInfo, UseErrorPageReturn } from './types';

/**
 * ErrorPageコンポーネントのビジネスロジックを管理するカスタムフック
 */
export const useErrorPage = (): UseErrorPageReturn => {
  const navigate = useNavigate();
  const [errorInfo, setErrorInfo] = useState<ErrorInfo>({
    message: 'システムエラーが発生しました',
    status: 500,
    path: '不明',
    timestamp: new Date().toISOString(),
    stack: '',
    data: {}
  });
  const [showTrace, setShowTrace] = useState<boolean>(false);

  // エラー情報の取得を安全に行う
  const safelyFetchErrorInfo = useCallback(() => {
    try {
      const storedError = sessionStorage.getItem('app_error');
      if (storedError) {
        const parsedError = JSON.parse(storedError);
        setErrorInfo(prevState => ({
          ...prevState,
          ...parsedError
        }));
        // エラー情報をクリア
        sessionStorage.removeItem('app_error');
      }
    } catch (error) {
      // エラーメッセージを文字列化して渡す
      const errorMessage = error instanceof Error
        ? `エラー情報の取得に失敗: ${error.message}`
        : 'エラー情報の取得に失敗しました';
      
      // エラー情報の取得に失敗しても基本的なUI表示は維持
    }
  }, []);

  useEffect(() => {
    safelyFetchErrorInfo();
  }, [safelyFetchErrorInfo]);

  const handleGoToLogin = () => {
    // ログイン画面に移動
    try {
      navigate('/login');
    } catch (error) {
      // エラーメッセージを文字列化して渡す
      const errorMessage = error instanceof Error
        ? `ログイン画面遷移中にエラーが発生: ${error.message}`
        : 'ログイン画面遷移中にエラーが発生しました';
      
      // 最悪の場合はリロード
      window.location.href = '/login';
    }
  };

  const handleContactSupport = () => {
    // サポートメールへのリンクを開く
    const subject = encodeURIComponent(`[エラー報告] ${errorInfo.status}: ${errorInfo.message}`);
    const body = encodeURIComponent(
      `エラー情報:\n` +
      `ステータス: ${errorInfo.status}\n` +
      `発生時刻: ${formatTimestamp(errorInfo.timestamp)}\n` +
      `URL: ${errorInfo.path || window.location.pathname}\n\n` +
      `エラー詳細:\n${errorInfo.stack || '詳細情報なし'}`
    );
    
    window.open(`mailto:surgilink.medtube@gmail.com?subject=${subject}&body=${body}`);
  };

  const toggleTrace = () => {
    setShowTrace(prev => !prev);
  };

  const formatTimestamp = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return '不明な時間';
      }
      return date.toLocaleString('ja-JP');
    } catch (e) {
      return '不明な時間';
    }
  };

  const getErrorStatusText = (status: number): string => {
    const statusMap: Record<number, string> = {
      400: 'Bad Request',
      401: '認証エラー',
      403: 'アクセス権限エラー',
      404: 'ページが見つかりません',
      500: 'サーバーエラー',
      502: 'ゲートウェイエラー',
      503: 'サービス利用不可',
      504: 'ゲートウェイタイムアウト'
    };
    return statusMap[status] || 'エラー';
  };

  // エラーコードに基づいてアイコンを選択
  const renderErrorIcon = () => {
    // アイコンコンポーネントはErrorPage.tsxで定義されているため、
    // ここではstatus値を返すだけにして、実際のコンポーネントレンダリングは
    // ErrorPage.tsxで行う
    return errorInfo.status === 404 ? 'ERROR_404' : 'ERROR_500';
  };

  return {
    errorInfo,
    showTrace,
    toggleTrace,
    handleGoToLogin,
    handleContactSupport,
    formatTimestamp,
    getErrorStatusText,
    renderErrorIcon
  };
}; 